// Library Imports
import { useState, useEffect } from "react";
import { Tooltip, Typography, message } from "antd";
import { useDispatch, useSelector } from "react-redux";

// Local Imports
import Checkbox from "../../../Common/Components/Checkbox";
import { AUTOMATION_LISTS } from "constants/types/automationTypes";
import { IListData, ILists, ParamsObjectType } from "modules/Automation/interfaces";
import SentEmailSearch from "modules/Campaigns/Emails/Components/SentEmailSearch";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";
import Input from "../../../Common/Components/Input";
import Message from "modules/Common/Components/Message";
import TickNormal from "../../../../assets/img/icons/tickNormal.svg";
import crossNormal from "../../../../assets/img/icons/crossNormal.svg";
import { RootState } from "store";
import { ControlNameEnum } from "../../interfaces";
import {
  addNode,
  clearDropArea,
  addActionModal,
  editNode,
  listsAPI,
  setSelectedlistIdsAction,
  resetWorkflowLists,
} from "../../actions";
import { findDropAreaData } from "../../utils";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import styled from "styled-components";
import { createNewListInWorkflow } from "modules/Campaigns/Audience/Lists/actions";
import AutomationListsSkeleton from "modules/Content/Shared/AutomationListsSkeleton";

type IListDataProps = {
  id: string;
  enableSaveButton: (value: boolean) => void;
  isNewList: boolean;
  setIsNewList: (value: boolean) => void;
  createNewListName: string;
  setCreateNewlistName: (value: string) => void;
};

const ListsData = (props: IListDataProps) => {
  // Props
  const { id, enableSaveButton, isNewList, setIsNewList, createNewListName, setCreateNewlistName } = props;

  // Redux States
  const lists: ILists = useSelector((state: RootState) => state.automation.lists);
  const listData: IListData[] = useSelector((state: RootState) => state.automation.lists.data);
  const listsLoading: boolean = useSelector((state: RootState) => state.automation.lists.loading);
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const newListCreatedSuccess: boolean = useSelector(
    (state: RootState) => state.automation.lists.newListCreatedSuccess
  );
  const newListCreatedError: boolean = useSelector(
    (state: RootState) => state.automation.lists.newListCreatedError
  );

  // Local States
  const [isSearchedList, setIsSearchedList] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const [selectedLists, setSelectedLists] = useState<IListData[]>([]);
  const [ellipsisState, setEllipsistate] = useState(false);
  const [disableInput, setDisableInput] = useState(false);

  // Local Variables
  let listsData: IListData[] = isSearchedList ? lists.searchedData : lists.data;
  const { Paragraph } = Typography;
  const dispatch = useDispatch();

  // Component Logic
  useEffect(() => {
    if (newListCreatedSuccess) {
      setTimeout(() => {
        dispatch({
          type: AUTOMATION_LISTS.CREATED_SUCCESS_STATE,
          payload: false,
        });
      }, 3000);
      setDisableInput(false);
      setIsNewList(false);
      setCreateNewlistName("");
      message.success("Your list has been added successfully!");
    }
  }, [newListCreatedSuccess]);

  useEffect(() => {
    if (newListCreatedError) {
      setTimeout(() => {
        dispatch({
          type: AUTOMATION_LISTS.CREATED_ERROR_STATE,
          payload: false,
        });
      }, 3000);
      setDisableInput(false);
      message.error("List already exists with that name!");
    }
  }, [newListCreatedError]);

  // useEffect(() => {
  //   !isNewList ? setDisableInput(true) : setDisableInput(false);
  // }, [isNewList]);

  const getPagination = (reset?: boolean, searchValue?: string) => {
    let params: ParamsObjectType = {
      limit: 15,
      offset: reset ? 0 : listsData.length,
    };
    if (reset) resetWorkflowLists();
    if (searchValue) {
      params.list_name = searchValue;
    }
    if (isListModalOpen && itemData) {
      console.log('if inside isListModal')
      let listsIds = itemData?.list?.map((list: IListData) => list.list_id)
      // let listsIds = [9, 10, 11]; //* for testing purpose
      params.workflow_list_ids = listsIds.join(',')
    }
    return params;
  };

  useEffect(() => {
    listsData = isSearchedList ? lists.searchedData : lists.data;
  }, [listData]);

  const updateListSearchTermInStore = (value: string) => {
    dispatch({
      type: AUTOMATION_LISTS.SET_SEARCH_TERM,
      payload: value,
    });
    dispatch({ type: AUTOMATION_LISTS.RESET_SEARCH_DATA });
    // if (value.trim()) listsAPI(value);
  };

  const listSearchHandler = (value: string) => {
    updateListSearchTermInStore(value);
    setSearchTerm(value);
    setIsSearchedList(true);
    if (!listsLoading) listsAPI(getPagination(true, value));
  };

  const resetListSearchHandler = () => {
    updateListSearchTermInStore("");
    setSearchTerm("");
    setIsSearchedList(false);
  };

  // useEffect to set the data of edit Node
  const isListModalOpen = modal.visible === true && modal.tab === ControlNameEnum.ADD_LIST;
  let dependencies = itemData && isListModalOpen;
  useEffect(() => {
    if (isListModalOpen && itemData) {
      setSelectedListIds([]);
      setSelectedLists([]);
      setSelectedlistIdsAction([]);
      let ids = itemData?.list?.map((list: IListData) => list.list_id);
      if (ids?.length) {
        listsAPI(getPagination(true));
        setSelectedListIds(ids);
        setSelectedLists(itemData?.list);
        setSelectedlistIdsAction(ids);
      }
      enableSaveButton(ids?.length > 0);
    } else if (isListModalOpen && selectedListIds?.length > 0) {
      setSelectedListIds([]);
      setSelectedLists([]);
      setSelectedlistIdsAction([]);
      enableSaveButton(false);
    }
  }, [dependencies]);

  useEffect(() => {
    if (modal.visible === false && selectedListIds.length > 0) {
      enableSaveButton(false);
      setSelectedListIds([]);
      setSelectedLists([]);
      setSelectedlistIdsAction([]);
    }
  }, [modal]);

  const checkboxHandler = (isChecked: boolean, clickedList: IListData) => {
    let checkedListIds: number[] = [];
    let checkedLists: IListData[] = [];
    if (isChecked) {
      checkedListIds = [...selectedListIds, clickedList?.list_id];
      checkedLists = [...selectedLists, clickedList];
    } else {
      checkedListIds = selectedListIds.filter((id) => id !== clickedList.list_id);
      checkedLists = selectedLists.filter((list) => list.list_id !== clickedList.list_id);
    }
    setSelectedListIds(checkedListIds);
    setSelectedLists(checkedLists);
    setSelectedlistIdsAction(checkedListIds);
    enableSaveButton(checkedListIds.length > 0);
  };

  const submitHandler = (event: any) => {
    event.preventDefault();
    let obj = findDropAreaData(ControlNameEnum.ADD_LIST);
    let selectedList: any = selectedLists;
    let title = `: ${selectedList.map((list: IListData) => ` ${list.list_name}`)}`;
    if (id) {
      // edit Node
      dispatch(
        editNode(id, {
          ...itemData,
          list: selectedList,
          nodeTitle: title,
        })
      );
    } else {
      dispatch(
        addNode({
          ...obj,
          list: selectedList,
          nodeTitle: title,
        })
      );
    }
    setSelectedListIds([]);
    setSelectedLists([]);
    setSelectedlistIdsAction([]);
    enableSaveButton(false);
    dispatch(addActionModal(false));
    dispatch(clearDropArea());
  };

  useEffect(() => {
    if (!listsLoading && lists.data.length === 0) listsAPI(getPagination(true));
  }, []);

  const handleNext = () => {
    if (!listsLoading) listsAPI(getPagination(false, searchTerm));
  };

  useEffect(() => {
    if (!actionModalVisible) {
      setSelectedListIds([]);
      setSelectedLists([]);
      setSelectedlistIdsAction([]);
      resetListSearchHandler();
    }
  }, [actionModalVisible]);

  const createNewListNameChangeHandler = (e: any) => {
    setCreateNewlistName(e.target.value);
  };

  const createNewListSaveHandler = (e: any) => {
    createNewListInWorkflow(createNewListName, isSearchedList, searchTerm);
    setDisableInput(true);
    //setIsNewList(false);
    //setCreateNewlistName("");
  };

  const createNewListCrossHandler = () => {
    setIsNewList(false);
    setCreateNewlistName("");
  };

  return (
    <Wrapper>
      <div className="search-wrapper">
        <SentEmailSearch
          handleSearch={listSearchHandler}
          reset={resetListSearchHandler}
          placeholder="Search lists"
        />
      </div>
      {lists.loading && listsData.length === 0 && (
        <AutomationListsSkeleton count={16} className="list-data-loader" />
      )}
      {isNewList ? (
        <li className="input-li">
          {/* Toggle Class d-none  */}
          <InputWrapper>
            <Input
              type="text"
              idInput="editable-input-list"
              value={createNewListName}
              onChange={createNewListNameChangeHandler}
              disabled={disableInput}
              placeholder="Enter List Name"
            />
            <span
              onClick={createNewListSaveHandler}
              className={`save ${createNewListName?.trim()?.length === 0 ? "disabled" : ""}`}
              data-qa="qa-save-name"
            >
              <img src={TickNormal} alt="save" />
            </span>
            <span onClick={createNewListCrossHandler} className={`cancel`} data-qa="qa-cancel-name">
              <img src={crossNormal} alt="cancel" />
            </span>
          </InputWrapper>
        </li>
      ) : null}
      {listsData.length > 0 ? (
        <form id={`${ControlNameEnum.ADD_LIST}`} onSubmit={submitHandler}>
          <ul className="email-list" id="contact_lists_wf">
            <InfiniteLoader
              dataLength={listsData.length + 1}
              // hasMore={
              //   isSearchedList ? totalCount > listsData.length : totalCount > listsData.length
              // }
              hasMore={true}
              next={handleNext}
              scrollableTarget={"contact_lists_wf"}
              loader={listsLoading && listsData.length > 0 ? <AutomationListsSkeleton count={10} /> : <></>}
            >
              {/* {newListCreatedSuccess && (
                <div className="message-div-list">
                  <Message
                    message="Your list has been added successfully!"
                    label="Success"
                    marginTop="14"
                    cross={false}
                  />
                </div>
              )}
              {newListCreatedError && (
                <div className="message-div-list">
                  <Message
                    type="error"
                    message="List already exists with that name!"
                    label="Error"
                    marginTop="14"
                    cross={false}
                  />
                </div>
              )} */}

              {listsData.map((list: IListData) => {
                return (
                  <li key={list.list_id}>
                    <label className="w-100 automation-list-item">
                      <Checkbox
                        checked={selectedListIds.includes(list.list_id)}
                        onChange={(e: any) => checkboxHandler(e.target.checked, list)}
                      />
                      {/* <Tooltip placement="top" title={ellipsisState ? list.list_name : undefined}> */}
                      <Paragraph className="list-name" ellipsis={{ rows: 1, tooltip: list.list_name }}>
                        {list.list_name}
                      </Paragraph>
                      {/* </Tooltip> */}
                    </label>
                  </li>
                );
              })}
            </InfiniteLoader>
          </ul>
          {selectedListIds.length ? (
            <div className={`list-count ${isNewList ? "new-list-count" : ""}`}>
              <span> {selectedListIds.length} list(s) selected</span>
            </div>
          ) : null}
        </form>
      ) : !isNewList ? (
        listsData.length === 0 && !listsLoading ? (
          <EmptyDiv description={isSearchedList ? "No lists found against your search." : "No lists available."} />
        ) : null
      ) : null}
    </Wrapper>
  );
};

export default ListsData;

const EmptyDiv = styled(EmptyTabInfo)`
  width: 100%;
  height: 415px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    height: 320px;
  }
  &.paginated {
    height: auto;
  }
`;

const Wrapper = styled.div`
  position: relative;
  .message-div-list {
    left: -25px;
    width: calc(100% - 40px);
    > div {
      padding: 3px 10px;
      margin-top: 0;
      border-radius: 6px;
    }
  }
  .automation-list-item {
    display: flex;
    align-items: center;
  }
  .list-name {
    height: auto;
    width: 100%;
  }
  .new-list-count {
    margin-top: -55px;
    margin-right: 5px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  label {
    position: absolute;
    right: 0;
    top: -25px;
    margin: 0;
  }
  input {
    padding-right: 70px;
    width: 96%;
    color: #424b50;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    font-family: "Roboto";
  }
  .save {
    position: absolute;
    top: 60px;
    right: 40px;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      top: 50px;
    }
    img {
      vertical-align: middle;
      max-width: 20px;
    }
    &.disabled {
      pointer-events: none;
      img {
        opacity: 0.5;
      }
    }
  }
  .cancel {
    position: absolute;
    top: 60px;
    right: 70px;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      top: 50px;
    }
    img {
      vertical-align: middle;
      max-width: 20px;
      padding: 2px;
    }
  }
  .cdisable {
    pointer-events: none;
    opacity: 0.5;
  }
`;
